/**
 * Company facets, scoped the same way as procurement.
 *
 * A municipality dossier lists the companies seated in it; a person's
 * dossier lists the ones they hold. Both are this facet set with a scope,
 * so both get filtering and paging for free instead of dumping every row.
 */

import type { FacetSet } from './types';

const FLAGS = [
  { key: 'won', label: 'печелили поръчка', countsFrom: 'flags', countValue: 'tenders' },
  { key: 'subsidised', label: 'получавали субсидия', countsFrom: 'flags', countValue: 'subsidies' },
  { key: 'debt', label: 'с данъчни задължения', countsFrom: 'flags', countValue: 'debt' },
  { key: 'paid', label: 'плащани от бюджета', countsFrom: 'flags', countValue: 'treasury' },
] as const;

/* Cumulative thresholds, not buckets: a company paid 2m лв belongs under
   "1m and above" as much as under "100k and above". Nesting them is what
   makes the sidebar answer "who was paid seriously" in one click. */
const TREASURY_BANDS = [
  { value: '10000', label: 'над 10 хил. лв' },
  { value: '100000', label: 'над 100 хил. лв' },
  { value: '1000000', label: 'над 1 млн. лв' },
  { value: '10000000', label: 'над 10 млн. лв' },
] as const;

type FlagKey = (typeof FLAGS)[number]['key'];

const LIST_KEYS = ['legalForms', 'provinces'] as const;
type ListKey = (typeof LIST_KEYS)[number];

export type CompanyState =
  & Record<ListKey, string[]>
  & Record<FlagKey, boolean>
  & { search: string; treasury: string };

export interface CompanyScope {
  seatKeys?: string[];
  ownerHashes?: string[];
}

export const COMPANY_GROUPS = [
  { key: 'legalForms', title: 'Правна форма', kind: 'check', counts: 'legalForms' },
  { key: 'provinces', title: 'Област на седалището', kind: 'check', counts: 'provinces' },
  { key: 'flags', title: 'Индикатори', kind: 'flags', items: FLAGS },
  {
    key: 'treasury',
    title: 'Плащания от бюджета',
    kind: 'threshold',
    counts: 'treasuryBands',
    options: TREASURY_BANDS,
  },
] as const;

function emptyState(): CompanyState {
  const state = { search: '', treasury: '' } as CompanyState;
  for (const key of LIST_KEYS) state[key] = [];
  for (const flag of FLAGS) state[flag.key] = false;
  return state;
}

/* The unscoped list is the public-funds population, and its caption says
   so. Reading the Търговски регистър brought in the layer above those
   firms — holding companies that own them, banks that lend to them — and
   a holding company with no contracts of its own reads as a recipient
   when it sits in that list.

   A scoped list answers a different question. A person's dossier lists
   the companies they hold and a municipality's the ones seated in it;
   there the holding company is part of the answer, and hiding it would
   break the very chain the register was read for. So the filter follows
   the caption, not the data. */
function isPublicFundsList(scope: CompanyScope): boolean {
  return !scope.seatKeys?.length && !scope.ownerHashes?.length;
}

export function companyFacetSet(
  prefix = 'c.',
  scope: CompanyScope = {},
): FacetSet<CompanyState, Record<string, unknown>, Record<string, unknown>> {
  const publicOnly = isPublicFundsList(scope);
  return {
    prefix,
    groups: COMPANY_GROUPS,
    emptyState,

    fromParams: (get, getAll) => {
      const state = emptyState();
      state.search = get('q') ?? '';
      state.treasury = get('paidmin') ?? '';
      for (const key of LIST_KEYS) state[key] = getAll(key);
      for (const flag of FLAGS) state[flag.key] = get(flag.key) === '1';
      return state;
    },

    toParams: (state) => {
      const params: Record<string, string | string[] | null> = {
        q: state.search || null, paidmin: state.treasury || null,
      };
      for (const key of LIST_KEYS) params[key] = state[key].length ? state[key] : null;
      for (const flag of FLAGS) params[flag.key] = state[flag.key] ? '1' : null;
      return params;
    },

    activeCount: (state) => LIST_KEYS.reduce((sum, key) => sum + (state[key].length ? 1 : 0), 0)
      + FLAGS.filter((flag) => state[flag.key]).length
      + (state.treasury ? 1 : 0),

    toFilter: (state) => ({
      publicOnly,
      search: state.search || null,
      legalForms: state.legalForms,
      provinces: state.provinces,
      hasTenders: state.won ? true : null,
      hasSubsidies: state.subsidised ? true : null,
      hasDebt: state.debt ? true : null,
      hasTreasury: state.paid ? true : null,
      minTreasury: state.treasury ? Number(state.treasury) : null,
      ...(scope.seatKeys?.length ? { seatKeys: scope.seatKeys } : {}),
      ...(scope.ownerHashes?.length ? { ownerHashes: scope.ownerHashes } : {}),
    }),

    toWhere: (state) => {
      const clauses: Record<string, unknown>[] = [];
      if (publicOnly) clauses.push({ public_funds: true });
      if (scope.seatKeys?.length) clauses.push({ seatedIn_SOME: { key_IN: scope.seatKeys } });
      if (scope.ownerHashes?.length) {
        // held either as owner or as manager — the dossier asks "connected
        // to this person", not "owned by" specifically
        clauses.push({
          OR: [
            { owners_SOME: { person_hash_IN: scope.ownerHashes } },
            { managers_SOME: { person_hash_IN: scope.ownerHashes } },
          ],
        });
      }
      if (state.search) {
        clauses.push({
          OR: [
            { name_CONTAINS: state.search },
            { uic_CONTAINS: state.search.replace(/\D/g, '') || state.search },
          ],
        });
      }
      if (state.legalForms.length) clauses.push({ legal_form_IN: state.legalForms });
      if (state.provinces.length) {
        clauses.push({ seatedIn_SOME: { province_IN: state.provinces } });
      }
      if (state.won) clauses.push({ tendersWonAggregate: { count_GT: 0 } });
      if (state.subsidised) clauses.push({ subsidiesAggregate: { count_GT: 0 } });
      if (state.debt) clauses.push({ taxDebtsAggregate: { count_GT: 0 } });
      if (state.paid) clauses.push({ treasuryPaymentsAggregate: { count_GT: 0 } });
      const minPaid = Number(state.treasury);
      if (minPaid > 0) clauses.push({ treasuryPayments_SOME: { amount_GTE: minPaid } });

      if (!clauses.length) return null;
      return clauses.length === 1 ? clauses[0]! : { AND: clauses };
    },
  };
}
